import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Slider, Tooltip } from '@mui/material';

import { Check, ChevronDown, Loader2 } from 'lucide-react';

import { useAppSelector } from '../../../hooks';
import { httpGET } from '../../../services/http';
import { getUser } from '../../../store/selectors/user.selector.ts';
import { green01 } from '../../../theme.ts';
import {
  MARKET_MATCH_COLOR_TEMP_MAX_K,
  MARKET_MATCH_COLOR_TEMP_MIN_K,
  MARKET_MATCH_COLOR_TEMP_STEP_K,
  type MarketMatchPrefilterRequest,
  type MarketMatchPrefilterState,
  buildMarketMatchPrefilterRequest,
  createDefaultMarketMatchPrefilterState,
} from '../../../utils/marketMatchPrefilters';

type ManufacturerItem = { id: number; name: string };

const toManufacturerItems = (raw: unknown): ManufacturerItem[] =>
  (Array.isArray(raw) ? raw : []).map((entry: { id: number | string; name: string }) => ({
    id: Number(entry.id),
    name: entry.name,
  }));

type MarketMatchPrefiltersPanelProps = {
  onStart: (filters: MarketMatchPrefilterRequest) => void;
  /** Renders the filters as a form with this id and drops the built-in start button, so a
   *  submit button elsewhere (the workspace panel footer) can start the search. */
  formId?: string;
  /** Reports whether the filters are valid, for that external button. */
  onCanStartChange?: (canStart: boolean) => void;
};

export function MarketMatchPrefiltersPanel({ onStart, formId, onCanStartChange }: MarketMatchPrefiltersPanelProps) {
  const { t } = useTranslation();
  const user = useAppSelector(getUser);
  const isMarketMatchStarting = useAppSelector((state) => state.workspace.isMarketMatchStarting);
  const [manufacturers, setManufacturers] = useState<ManufacturerItem[]>([]);
  const [isLoadingManufacturers, setIsLoadingManufacturers] = useState(true);
  const [isManufacturerDropdownOpen, setIsManufacturerDropdownOpen] = useState(false);
  const [state, setState] = useState<MarketMatchPrefilterState>(createDefaultMarketMatchPrefilterState);
  const manufacturerDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    void httpGET('/catalogue/manufacturers')
      .then(({ data }) => {
        if (cancelled) return;
        const items = toManufacturerItems(data);
        setManufacturers(items);
      })
      .catch((err) => console.error('Failed to load manufacturers:', err))
      .finally(() => {
        if (!cancelled) setIsLoadingManufacturers(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!manufacturerDropdownRef.current?.contains(event.target as Node)) {
        setIsManufacturerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  const allManufacturerIds = useMemo(() => manufacturers.map((manufacturer) => manufacturer.id), [manufacturers]);
  const favoriteManufacturerIds = useMemo(
    () => new Set(user?.favoriteManufacturerIds ?? []),
    [user?.favoriteManufacturerIds],
  );

  const favoriteManufacturers = useMemo(() => {
    const byName = (a: ManufacturerItem, b: ManufacturerItem) => a.name.localeCompare(b.name);
    return manufacturers.filter((manufacturer) => favoriteManufacturerIds.has(manufacturer.id)).sort(byName);
  }, [favoriteManufacturerIds, manufacturers]);

  const regularManufacturers = useMemo(() => {
    const byName = (a: ManufacturerItem, b: ManufacturerItem) => a.name.localeCompare(b.name);
    return manufacturers.filter((manufacturer) => !favoriteManufacturerIds.has(manufacturer.id)).sort(byName);
  }, [favoriteManufacturerIds, manufacturers]);

  const selectedManufacturerIdSet = useMemo(
    () => new Set(state.selectedManufacturerIds),
    [state.selectedManufacturerIds],
  );

  const areAllManufacturersSelected = useMemo(
    () => allManufacturerIds.length > 0 && allManufacturerIds.every((id) => selectedManufacturerIdSet.has(id)),
    [allManufacturerIds, selectedManufacturerIdSet],
  );

  const selectedManufacturerNames = useMemo(
    () =>
      manufacturers
        .filter((manufacturer) => selectedManufacturerIdSet.has(manufacturer.id))
        .map((manufacturer) => manufacturer.name),
    [manufacturers, selectedManufacturerIdSet],
  );

  const manufacturersLabel = useMemo(() => {
    if (isLoadingManufacturers) return t('common.loading', 'Loading...');
    if (manufacturers.length === 0) return t('workspace.marketMatchPrefiltersNoManufacturers');
    if (areAllManufacturersSelected) return t('workspace.marketMatchPrefiltersIncludeAll');
    if (selectedManufacturerNames.length === 0) return t('workspace.marketMatchPrefiltersSelectManufacturers');
    if (selectedManufacturerNames.length <= 2) return selectedManufacturerNames.join(', ');
    return t('workspace.marketMatchPrefiltersSelectedCount', { count: selectedManufacturerNames.length });
  }, [areAllManufacturersSelected, isLoadingManufacturers, manufacturers.length, selectedManufacturerNames, t]);

  const selectAllManufacturers = () => {
    setState((prev) => ({ ...prev, selectedManufacturerIds: allManufacturerIds }));
  };

  const toggleManufacturer = (id: number) => {
    setState((prev) => {
      const selected = new Set(prev.selectedManufacturerIds);
      if (selected.has(id)) {
        selected.delete(id);
      } else {
        selected.add(id);
      }
      return { ...prev, selectedManufacturerIds: Array.from(selected) };
    });
  };

  const handleColorTemperatureRangeChange = (_event: Event, value: number | number[]) => {
    if (!Array.isArray(value) || value.length !== 2) return;
    const [nextMin, nextMax] = value;
    setState((prev) => ({
      ...prev,
      minColorTemperature: nextMin,
      maxColorTemperature: nextMax,
    }));
  };

  const canStart = !isLoadingManufacturers && state.selectedManufacturerIds.length > 0 && !isMarketMatchStarting;

  useEffect(() => {
    onCanStartChange?.(canStart);
  }, [canStart, onCanStartChange]);

  const Container = formId ? 'form' : 'div';

  return (
    <Container
      id={formId}
      onSubmit={
        formId
          ? (event: FormEvent) => {
              event.preventDefault();
              if (canStart) onStart(buildMarketMatchPrefilterRequest(manufacturers, state));
            }
          : undefined
      }
      className={formId ? 'px-4 py-4 space-y-5' : 'px-3 py-6 space-y-5 bg-black'}
    >
      <div>
        <p style={{ color: green01 }} className="text-lg font-semibold">
          {t('workspace.searchingMarket')}
        </p>
        <p className="mt-2 text-stone-400 text-sm leading-relaxed">{t('workspace.marketMatchDescription')}</p>
      </div>

      <div className="space-y-2">
        <p className="text-[11px] font-medium text-gray-300">{t('workspace.marketMatchPrefiltersManufacturers')}</p>
        <div ref={manufacturerDropdownRef} className="relative">
          <button
            type="button"
            className="w-full rounded-md border border-stone-700 bg-[#171717] px-3 py-2 text-left text-sm text-stone-200 hover:border-stone-500 transition-colors disabled:opacity-70"
            disabled={isLoadingManufacturers || manufacturers.length === 0}
            onClick={() => setIsManufacturerDropdownOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={isManufacturerDropdownOpen}
            aria-controls={isManufacturerDropdownOpen ? 'market-match-manufacturer-dropdown' : undefined}
          >
            <span className="flex items-center justify-between gap-3">
              <span className="truncate">{manufacturersLabel}</span>
              <ChevronDown
                size={16}
                className={`text-stone-400 transition-transform ${isManufacturerDropdownOpen ? 'rotate-180' : ''}`}
              />
            </span>
          </button>

          {isManufacturerDropdownOpen && manufacturers.length > 0 && (
            <div
              id="market-match-manufacturer-dropdown"
              role="listbox"
              className="absolute z-20 mt-2 w-full rounded-md border border-stone-700 bg-[#111111] p-1 shadow-2xl"
            >
              <button
                type="button"
                className="w-full flex items-center gap-3 rounded px-2 py-2 text-sm text-stone-200 hover:bg-stone-800/80 transition-colors"
                onClick={selectAllManufacturers}
              >
                <span
                  className={`h-4 w-4 rounded-sm flex items-center justify-center transition-colors duration-200 ${
                    areAllManufacturersSelected ? 'bg-lime-400' : 'bg-transparent border border-stone-500'
                  }`}
                >
                  {areAllManufacturersSelected && <Check className="h-3 w-3 text-black" />}
                </span>
                {t('workspace.marketMatchPrefiltersIncludeAll')}
              </button>

              <div className="my-1 h-px bg-stone-800" />

              <div className="max-h-[220px] overflow-y-auto space-y-2 pr-1">
                {favoriteManufacturers.length > 0 && (
                  <div className="space-y-0.5">
                    <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-stone-500">
                      {t('catalogue.favorites')}
                    </p>
                    {favoriteManufacturers.map((manufacturer) => {
                      const isSelected = selectedManufacturerIdSet.has(manufacturer.id);
                      return (
                        <button
                          key={manufacturer.id}
                          type="button"
                          className="w-full flex items-center gap-3 rounded px-2 py-2 text-sm text-stone-200 hover:bg-stone-800/80 transition-colors"
                          onClick={() => toggleManufacturer(manufacturer.id)}
                        >
                          <span
                            className={`h-4 w-4 rounded-sm flex items-center justify-center transition-colors duration-200 ${
                              isSelected ? 'bg-lime-400' : 'bg-transparent border border-stone-500'
                            }`}
                          >
                            {isSelected && <Check className="h-3 w-3 text-black" />}
                          </span>
                          <span className="truncate">{manufacturer.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {regularManufacturers.length > 0 && (
                  <div className="space-y-0.5">
                    <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-stone-500">
                      {t('catalogue.allManufacturers')}
                    </p>
                    {regularManufacturers.map((manufacturer) => {
                      const isSelected = selectedManufacturerIdSet.has(manufacturer.id);
                      return (
                        <button
                          key={manufacturer.id}
                          type="button"
                          className="w-full flex items-center gap-3 rounded px-2 py-2 text-sm text-stone-200 hover:bg-stone-800/80 transition-colors"
                          onClick={() => toggleManufacturer(manufacturer.id)}
                        >
                          <span
                            className={`h-4 w-4 rounded-sm flex items-center justify-center transition-colors duration-200 ${
                              isSelected ? 'bg-lime-400' : 'bg-transparent border border-stone-500'
                            }`}
                          >
                            {isSelected && <Check className="h-3 w-3 text-black" />}
                          </span>
                          <span className="truncate">{manufacturer.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {manufacturers.length === 0 && !isLoadingManufacturers && (
          <p className="text-stone-500 text-xs">{t('workspace.marketMatchPrefiltersNoManufacturers')}</p>
        )}
        {!isLoadingManufacturers && state.selectedManufacturerIds.length === 0 && manufacturers.length > 0 && (
          <p className="text-stone-500 text-xs">{t('workspace.marketMatchPrefiltersSelectManufacturers')}</p>
        )}
      </div>

      <div className="space-y-3">
        <p className="text-[11px] font-medium text-gray-300">{t('workspace.marketMatchPrefiltersColorTemperature')}</p>
        <p className="text-[11px] text-stone-500">
          {t('workspace.marketMatchPrefiltersColorTemperatureRange', {
            min: state.minColorTemperature,
            max: state.maxColorTemperature,
          })}
        </p>
        <div className="w-[88%] mx-auto">
          <Slider
            size="small"
            value={[state.minColorTemperature, state.maxColorTemperature]}
            onChange={handleColorTemperatureRangeChange}
            min={MARKET_MATCH_COLOR_TEMP_MIN_K}
            max={MARKET_MATCH_COLOR_TEMP_MAX_K}
            step={MARKET_MATCH_COLOR_TEMP_STEP_K}
            disableSwap
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}K`}
            sx={{
              color: green01,
              px: 0.5,
              '& .MuiSlider-rail': { backgroundColor: '#3f3f46', opacity: 1 },
              '& .MuiSlider-track': { border: 'none' },
              '& .MuiSlider-thumb': {
                width: 16,
                height: 16,
                border: '2px solid #111',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 8px rgba(179, 236, 73, 0.18)',
                },
              },
              '& .MuiSlider-valueLabel': {
                backgroundColor: '#111',
                color: '#fff',
                borderRadius: '6px',
              },
            }}
          />
        </div>
      </div>

      {!formId && (
        <Tooltip
          title={
            !canStart && !isMarketMatchStarting && state.selectedManufacturerIds.length === 0
              ? t('workspace.marketMatchPrefiltersSelectManufacturers', 'Pick at least one manufacturer')
              : ''
          }
          arrow
          placement="top"
        >
          <span className="inline-block w-full">
            <button
              className="w-full px-6 py-3 rounded-full text-black font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
              style={{ backgroundColor: green01 }}
              disabled={!canStart}
              onClick={() => onStart(buildMarketMatchPrefilterRequest(manufacturers, state))}
            >
              {isMarketMatchStarting && <Loader2 size={16} className="animate-spin" />}
              {isMarketMatchStarting ? t('workspace.preparing') : t('workspace.startMarketMatch')}
            </button>
          </span>
        </Tooltip>
      )}
    </Container>
  );
}
